/* ============================================================
   Gadgeticloud — Parcel Telemetry (client)
   Vanilla JS. No framework. No theme dependency.
   ============================================================ */
(function () {
  'use strict';

  const root = document.querySelector('[data-gtc-root]');
  if (!root) return;

  const WEBHOOK_URL = (root.dataset.webhookUrl || '').trim();
  const TURNSTILE_SITEKEY = (root.dataset.turnstileSitekey || '').trim();

  const form = root.querySelector('#gtc-form');
  const resultEl = root.querySelector('#gtc-result');
  const submitBtn = form.querySelector('.gtc-submit');
  const turnstileMount = root.querySelector('#gtc-turnstile-mount');

  // ---------- Turnstile ----------
  let turnstileWidgetId = null;
  let cachedTurnstileToken = '';

  function loadTurnstile() {
    if (!TURNSTILE_SITEKEY) return;
    if (window.turnstile) { renderTurnstile(); return; }
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__gtcTurnstileLoaded&render=explicit';
    s.async = true; s.defer = true;
    document.head.appendChild(s);
    window.__gtcTurnstileLoaded = renderTurnstile;
  }
  function renderTurnstile() {
    if (!window.turnstile || turnstileWidgetId) return;
    turnstileWidgetId = window.turnstile.render(turnstileMount, {
      sitekey: TURNSTILE_SITEKEY,
      theme: 'light',
      size: 'flexible',
      callback: (token) => { cachedTurnstileToken = token; }
    });
  }
  function resetTurnstile() {
    cachedTurnstileToken = '';
    if (window.turnstile && turnstileWidgetId) {
      window.turnstile.reset(turnstileWidgetId);
    }
  }

  // ---------- Submit ----------
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const orderRaw = form.order_number.value.trim().replace(/^#/, '');
    const email = form.email.value.trim();

    if (orderRaw.length < 4 || !email.includes('@')) {
      flashFieldError(orderRaw.length < 4 ? 'gtc-order' : 'gtc-email');
      return;
    }

    if (!WEBHOOK_URL && !window.__GTC_MOCK_FETCH) {
      renderState({
        state: 'service_warming',
        order: null,
        shipments: []
      });
      return;
    }

    if (TURNSTILE_SITEKEY && !cachedTurnstileToken && !window.__GTC_MOCK_FETCH) {
      // try to get token, if widget hasn't completed yet
      renderState({ state: 'turnstile_pending' });
      return;
    }

    setLoading(true);
    renderSkeleton();

    try {
      const res = await fetchTracking({
        order_number: orderRaw,
        email: email,
        turnstile_token: cachedTurnstileToken
      });
      renderState(res);
    } catch (err) {
      console.error('[gtc] fetch error:', err);
      renderState({ state: 'network_error' });
    } finally {
      setLoading(false);
      resetTurnstile();
    }
  });

  async function fetchTracking(payload) {
    // Mock hook for local Playwright preview
    if (window.__GTC_MOCK_FETCH) {
      return window.__GTC_MOCK_FETCH(payload);
    }
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 20000);
    const r = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: ctrl.signal
    });
    clearTimeout(tid);
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  }

  function setLoading(loading) {
    submitBtn.dataset.loading = loading ? 'true' : 'false';
  }
  function flashFieldError(id) {
    const field = root.querySelector('#' + id);
    if (!field) return;
    const wrap = field.closest('.gtc-field');
    wrap.classList.add('gtc-field--error');
    field.focus();
    setTimeout(() => wrap.classList.remove('gtc-field--error'), 1800);
  }

  // ---------- Renderers ----------
  function show(html) {
    resultEl.hidden = false;
    resultEl.classList.remove('gtc-result__enter');
    void resultEl.offsetWidth; // restart animation
    resultEl.innerHTML = html;
    resultEl.classList.add('gtc-result__enter');
  }

  function renderSkeleton() {
    show(`
      <div class="gtc-card">
        <div class="gtc-skeleton">
          <div class="gtc-skeleton__row gtc-skeleton__row--title"></div>
          <div class="gtc-skeleton__row gtc-skeleton__row--short"></div>
          <div class="gtc-skeleton__row gtc-skeleton__row--strip"></div>
          <div class="gtc-skeleton__row"></div>
          <div class="gtc-skeleton__row gtc-skeleton__row--line"></div>
          <div class="gtc-skeleton__row gtc-skeleton__row--short"></div>
          <div class="gtc-skeleton__row gtc-skeleton__row--line"></div>
        </div>
      </div>
    `);
  }

  function renderState(res) {
    if (!res || typeof res !== 'object') { return renderError('network_error'); }
    switch (res.state) {
      case 'in_transit':
      case 'delivered':
      case 'exception':
        return renderTimeline(res);
      case 'awaiting_dispatch':
        return renderAwaiting(res);
      case 'not_found':
      case 'invalid_input':
        return renderError('not_found');
      case 'turnstile_pending':
        return renderError('turnstile');
      case 'service_warming':
        return renderError('warming');
      case 'network_error':
      default:
        return renderError('network_error');
    }
  }

  // ---------- State renderers ----------
  function renderTimeline(res) {
    const order = res.order || {};
    const ship = (res.shipments && res.shipments[0]) || {};
    const checkpoints = (ship.checkpoints || []).slice().sort(byDateAsc);
    const totalSteps = checkpoints.length;
    const progressPct = res.state === 'delivered' ? 100
      : (totalSteps > 0 ? Math.min(95, Math.round((totalSteps - 1) / totalSteps * 100)) : 30);

    const pillClass = pillClassFor(res.state);
    const pillLabel = pillLabelFor(res.state);
    const headlineTitle = headlineFor(res, ship);
    const headlineSub = subFor(res, ship);

    const orderStrip = `
      <div class="gtc-order-strip">
        ${stripItem('Order', order.name || '—')}
        ${stripItem('Placed', formatDate(order.created_at) || '—')}
        ${stripItem('Items', String(itemsCount(order)))}
        ${ship.expected_delivery ? stripItem('Expected', formatDate(ship.expected_delivery)) : ''}
      </div>
    `;

    const carrierStrip = ship.carrier ? `
      <div class="gtc-carrier">
        <div class="gtc-carrier__brand">
          <div class="gtc-carrier__logo">${carrierLogo(ship.carrier_slug || ship.carrier)}</div>
          <div class="gtc-carrier__text">
            <span class="gtc-carrier__name">${escapeHTML(ship.carrier)}</span>
            <span class="gtc-carrier__tracking">${escapeHTML(ship.tracking_number || '')}</span>
          </div>
        </div>
        ${ship.tracking_url
          ? `<a class="gtc-carrier__link" href="${escapeHTML(ship.tracking_url)}" target="_blank" rel="noopener">
              View on ${escapeHTML(ship.carrier)}
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 3h6v6M3 9l6-6" stroke="currentColor" stroke-width="1.4" stroke-linecap="square"/></svg>
            </a>`
          : `<span class="gtc-carrier__meta">Live carrier sync</span>`}
      </div>
    ` : '';

    const carrierNotice = ship.carrier_message ? `
      <div class="gtc-notice">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.4"/><path d="M8 4.5v4M8 11v.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="square"/></svg>
        <span>${escapeHTML(ship.carrier_message)}${ship.tracking_url ? ` &mdash; the parcel was handed to the carrier; their tracking page may show more detail.` : ''}</span>
      </div>
    ` : '';

    const timeline = `
      <div class="gtc-timeline">
        <div class="gtc-timeline__rail"></div>
        <div class="gtc-timeline__progress" style="--gtc-progress: ${progressPct}%"></div>
        ${checkpoints.length === 0
          ? `<div class="gtc-checkpoint gtc-checkpoint--current">
              <div class="gtc-checkpoint__dot"></div>
              <p class="gtc-checkpoint__time">Awaiting first scan</p>
              <p class="gtc-checkpoint__msg">Tracking registered — waiting for the carrier's first checkpoint.</p>
            </div>`
          : checkpoints.map((c, i) => renderCheckpoint(c, i, checkpoints.length, res.state)).join('')}
      </div>
    `;

    show(`
      <div class="gtc-card">
        <div class="gtc-status">
          <div class="gtc-status__primary">
            <span class="gtc-status__label">${escapeHTML(order.name || 'Parcel')} · Telemetry</span>
            <h2 class="gtc-status__title">${escapeHTML(headlineTitle)}</h2>
            ${headlineSub ? `<p class="gtc-status__sub">${escapeHTML(headlineSub)}</p>` : ''}
          </div>
          <span class="gtc-pill ${pillClass} ${res.state === 'in_transit' ? 'gtc-pill--live' : ''}">
            <span class="gtc-pill__dot"></span>
            ${escapeHTML(pillLabel)}
          </span>
        </div>
        ${orderStrip}
        ${carrierStrip}
        ${carrierNotice}
        ${timeline}
        <div class="gtc-result__footer-meta">
          <span>Source · Aftership / ${escapeHTML(ship.carrier || 'Carrier')}</span>
          <button class="gtc-refresh" type="button" data-gtc-refresh>
            <svg viewBox="0 0 16 16" fill="none"><path d="M2 8a6 6 0 1110.5 4M13.5 8H10m3.5 0V4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>
            Refresh
          </button>
        </div>
      </div>
    `);

    const refreshBtn = resultEl.querySelector('[data-gtc-refresh]');
    if (refreshBtn) refreshBtn.addEventListener('click', () => form.requestSubmit());
  }

  function renderCheckpoint(c, i, total, overallState) {
    const isLast = i === total - 1;
    let cls = 'gtc-checkpoint';
    if (isLast && overallState === 'delivered') cls += ' gtc-checkpoint--delivered-final';
    else if (isLast) cls += ' gtc-checkpoint--current';
    else cls += ' gtc-checkpoint--past';

    const checkSvg = `<svg viewBox="0 0 12 12" fill="none"><path d="M2.5 6l2.5 2.5L9.5 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="square" stroke-linejoin="miter"/></svg>`;
    const tickSvg = isLast && overallState !== 'delivered' ? '' : checkSvg;

    return `
      <div class="${cls}">
        <div class="gtc-checkpoint__dot">${tickSvg}</div>
        <p class="gtc-checkpoint__time">${escapeHTML(formatDateTime(c.datetime || c.date))}</p>
        <p class="gtc-checkpoint__msg">${escapeHTML(c.message || c.tag || '—')}</p>
        ${c.location ? `<p class="gtc-checkpoint__loc">${escapeHTML(c.location)}</p>` : ''}
      </div>
    `;
  }

  function renderAwaiting(res) {
    const order = res.order || {};
    const items = (order.line_items || []).map(li => `
      <li>
        <span class="gtc-items__name">${escapeHTML(li.title || li.name || 'Item')}</span>
        <span class="gtc-items__qty">×${li.quantity || 1}</span>
      </li>
    `).join('');

    show(`
      <div class="gtc-card">
        <div class="gtc-status">
          <div class="gtc-status__primary">
            <span class="gtc-status__label">${escapeHTML(order.name || 'Parcel')} · Telemetry</span>
            <h2 class="gtc-status__title">Awaiting dispatch.</h2>
            <p class="gtc-status__sub">Your order is confirmed and being prepared. We'll update this page the moment the carrier picks it up.</p>
          </div>
          <span class="gtc-pill gtc-pill--awaiting">
            <span class="gtc-pill__dot"></span>
            Awaiting dispatch
          </span>
        </div>
        <div class="gtc-order-strip">
          ${stripItem('Order', order.name || '—')}
          ${stripItem('Placed', formatDate(order.created_at) || '—')}
          ${stripItem('Items', String(itemsCount(order)))}
          ${stripItem('Status', 'Preparing')}
        </div>
        <div class="gtc-awaiting">
          <div class="gtc-awaiting__art">${awaitingSVG()}</div>
          <div>
            <h3 style="font-family:var(--gtc-font-display);font-weight:600;letter-spacing:-0.02em;margin:0 0 8px;font-size:20px;">What happens next?</h3>
            <p style="margin:0;color:var(--gtc-ink-2);font-size:14px;line-height:1.5;">
              Orders typically ship within 1&ndash;2 working days.
              Once handed to the carrier you'll receive a dispatch email, and tracking will appear here.
            </p>
            ${items ? `<ul class="gtc-items">${items}</ul>` : ''}
          </div>
        </div>
        <div class="gtc-result__footer-meta">
          <span>Source · Shopify order record</span>
          <button class="gtc-refresh" type="button" data-gtc-refresh>
            <svg viewBox="0 0 16 16" fill="none"><path d="M2 8a6 6 0 1110.5 4M13.5 8H10m3.5 0V4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/></svg>
            Refresh
          </button>
        </div>
      </div>
    `);
    const refreshBtn = resultEl.querySelector('[data-gtc-refresh]');
    if (refreshBtn) refreshBtn.addEventListener('click', () => form.requestSubmit());
  }

  function renderError(kind) {
    const map = {
      not_found: {
        title: "We couldn't find that order.",
        msg: "Double-check the order number and email — both must match what's on your confirmation email exactly. If you ordered with a different email, try that one.",
        icon: notFoundSVG()
      },
      network_error: {
        title: "Tracking service unavailable.",
        msg: "There was a problem reaching the tracking service. Please try again in a moment.",
        icon: networkSVG()
      },
      turnstile: {
        title: "Just a moment.",
        msg: "Bot-check is still loading. Please wait a couple of seconds and try again.",
        icon: shieldSVG()
      },
      warming: {
        title: "Tracking service warming up.",
        msg: "The live tracking backend is being set up. Check back shortly.",
        icon: shieldSVG()
      }
    };
    const e = map[kind] || map.network_error;
    show(`
      <div class="gtc-card">
        <div class="gtc-error-state">
          <div class="gtc-error-state__icon">${e.icon}</div>
          <h2 class="gtc-error-state__title">${e.title}</h2>
          <p class="gtc-error-state__msg">${e.msg}</p>
          <button class="gtc-link-btn" type="button" data-gtc-retry>Try again</button>
        </div>
      </div>
    `);
    const retryBtn = resultEl.querySelector('[data-gtc-retry]');
    if (retryBtn) retryBtn.addEventListener('click', () => {
      resultEl.hidden = true;
      form.querySelector('input').focus();
    });
  }

  // ---------- Helpers ----------
  function stripItem(label, value) {
    return `
      <div class="gtc-order-strip__item">
        <span class="gtc-order-strip__label">${escapeHTML(label)}</span>
        <span class="gtc-order-strip__value">${escapeHTML(value)}</span>
      </div>
    `;
  }
  function escapeHTML(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }
  function byDateAsc(a, b) {
    const da = new Date(a.datetime || a.date || 0).getTime();
    const db = new Date(b.datetime || b.date || 0).getTime();
    return da - db;
  }
  function formatDate(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso; }
  }
  function formatDateTime(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  }
  function itemsCount(order) {
    return (order.line_items || []).reduce((n, li) => n + (li.quantity || 1), 0) || 0;
  }
  function pillClassFor(state) {
    return ({
      in_transit: 'gtc-pill--transit',
      delivered: 'gtc-pill--delivered',
      awaiting_dispatch: 'gtc-pill--awaiting',
      exception: 'gtc-pill--exception'
    })[state] || 'gtc-pill--transit';
  }
  function pillLabelFor(state) {
    return ({
      in_transit: 'In transit',
      delivered: 'Delivered',
      awaiting_dispatch: 'Awaiting dispatch',
      exception: 'Exception'
    })[state] || 'Tracking';
  }
  function headlineFor(res, ship) {
    if (res.state === 'delivered') return 'Delivered.';
    if (res.state === 'exception') return 'Action required.';
    return 'On its way.';
  }
  function subFor(res, ship) {
    if (res.state === 'delivered' && ship.shipment_delivery_date) {
      return 'Delivered on ' + formatDate(ship.shipment_delivery_date) + '.';
    }
    if (res.state === 'in_transit' && ship.expected_delivery) {
      return 'Estimated arrival: ' + formatDate(ship.expected_delivery) + '.';
    }
    if (res.state === 'exception') {
      return 'The carrier reported a delivery issue — see latest update below.';
    }
    return 'Live carrier updates below.';
  }

  // ---------- SVG library ----------
  function awaitingSVG() {
    return `<svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="40" y="58" width="120" height="100" rx="6" fill="#fafafa" stroke="#0a0a0a" stroke-width="1.5"/>
      <path d="M40 88h120" stroke="#0a0a0a" stroke-width="1.5"/>
      <path d="M100 58v100" stroke="#0a0a0a" stroke-width="1.5" stroke-dasharray="3 3"/>
      <circle cx="100" cy="46" r="14" fill="#9914f2"/>
      <path d="M94 46l4 4 8-8" stroke="white" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter" fill="none"/>
      <circle cx="100" cy="180" r="3" fill="#9914f2"/>
      <circle cx="116" cy="180" r="3" fill="#ebebeb"/>
      <circle cx="84" cy="180" r="3" fill="#ebebeb"/>
    </svg>`;
  }
  function notFoundSVG() {
    return `<svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="35" cy="35" r="22" stroke="currentColor" stroke-width="2"/>
      <path d="M51 51l16 16" stroke="currentColor" stroke-width="2" stroke-linecap="square"/>
      <path d="M28 28l14 14M42 28L28 42" stroke="currentColor" stroke-width="2" stroke-linecap="square"/>
    </svg>`;
  }
  function networkSVG() {
    return `<svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M10 40c10-10 50-10 60 0M18 50c8-8 36-8 44 0M26 60c6-5 22-5 28 0" stroke="currentColor" stroke-width="2" stroke-linecap="square"/>
      <circle cx="40" cy="68" r="3" fill="currentColor"/>
      <path d="M58 22l14 14M72 22L58 36" stroke="currentColor" stroke-width="2" stroke-linecap="square"/>
    </svg>`;
  }
  function shieldSVG() {
    return `<svg viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M40 8l28 8v22c0 14-12 26-28 34-16-8-28-20-28-34V16l28-8z" stroke="currentColor" stroke-width="2" stroke-linejoin="miter"/>
      <path d="M28 38l8 8 16-16" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter"/>
    </svg>`;
  }

  // ---------- Carrier logos ----------
  function carrierLogo(slugOrName) {
    const key = String(slugOrName || '').toLowerCase().replace(/[^a-z]/g, '');
    const logos = {
      dhl: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#FFCC00"/><text x="30" y="13" text-anchor="middle" font-family="Arial Black,sans-serif" font-weight="900" font-size="11" fill="#D40511" letter-spacing="0.5">DHL</text></svg>`,
      dpd: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#414042"/><text x="30" y="13" text-anchor="middle" font-family="Arial Black,sans-serif" font-weight="900" font-size="11" fill="#DC0032">DPD</text></svg>`,
      ups: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#351C15"/><text x="30" y="13" text-anchor="middle" font-family="Arial Black,sans-serif" font-weight="900" font-size="11" fill="#FFB500">UPS</text></svg>`,
      royalmail: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#EE2722"/><text x="30" y="12" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="8" fill="#FFCC00">ROYAL MAIL</text></svg>`,
      evri: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#7B2CBF"/><text x="30" y="13" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="10" fill="#FFFFFF" letter-spacing="0.5">EVRI</text></svg>`,
      hermes: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#7B2CBF"/><text x="30" y="13" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="10" fill="#FFFFFF" letter-spacing="0.5">EVRI</text></svg>`,
      fedex: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#ffffff"/><text x="30" y="13" text-anchor="middle" font-family="Arial Black,sans-serif" font-weight="900" font-size="11"><tspan fill="#4D148C">Fed</tspan><tspan fill="#FF6600">Ex</tspan></text></svg>`,
      colissimo: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#FCDD00"/><text x="30" y="12" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="7" fill="#003366">COLISSIMO</text></svg>`,
      yunexpress: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#005BAC"/><text x="30" y="12" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="7" fill="#FFFFFF" letter-spacing="0.3">YUNEXPRESS</text></svg>`,
      yanwen: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#E60012"/><text x="30" y="13" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="9" fill="#FFFFFF">YANWEN</text></svg>`,
      usps: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#FFFFFF"/><text x="30" y="13" text-anchor="middle" font-family="Arial Black,sans-serif" font-weight="900" font-size="11" fill="#004B87">USPS</text></svg>`,
      tnt: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#FF6600"/><text x="30" y="13" text-anchor="middle" font-family="Arial Black,sans-serif" font-weight="900" font-size="11" fill="#FFFFFF">TNT</text></svg>`,
      sfexpress: `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#000000"/><text x="30" y="13" text-anchor="middle" font-family="Arial,sans-serif" font-weight="700" font-size="9" fill="#FFFFFF" letter-spacing="0.5">SF EXPRESS</text></svg>`
    };
    return logos[key] || `<svg viewBox="0 0 60 18" xmlns="http://www.w3.org/2000/svg"><rect width="60" height="18" fill="#0a0a0a"/><text x="30" y="13" text-anchor="middle" font-family="monospace" font-size="9" fill="#9914f2" letter-spacing="0.5">CARRIER</text></svg>`;
  }

  // ---------- Boot ----------
  loadTurnstile();

})();
